'use client';

import React from 'react';
import { Info } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * Info affordance for section headers and metric tiles across Agent Insights.
 * Renders a focusable Info icon that reveals an explanatory tooltip on hover or
 * keyboard focus. Self-contained — carries its own TooltipProvider so it works
 * anywhere without a provider ancestor.
 */
export function InfoTooltip({
  content,
  className,
  iconClassName,
  side = 'top',
  label = 'More information',
}: {
  /** Explanatory copy shown in the tooltip. */
  content: React.ReactNode;
  /** Layout classes for the trigger button (e.g. `shrink-0`). */
  className?: string;
  /** Size override for the icon. Defaults to `size-4` (16px). */
  iconClassName?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Accessible name for the trigger. Say what the tooltip explains: on a page
   *  with several of these, "More information" repeated is no name at all. */
  label?: string;
}) {
  return (
    <TooltipProvider delay={150}>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              aria-label={label}
              className={cn(
                'relative inline-flex items-center justify-center rounded-sm text-ds-text-subtle transition-colors hover:text-ds-text-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-border-subtler',
                // A 16px glyph is a 16px hit target. The pseudo-element grows
                // it to 32px without occupying any layout, so the icon still
                // sits tight against the heading beside it.
                'before:absolute before:-inset-2 before:content-[""]',
                className
              )}
            >
              {/* FILLED, and Phosphor rather than lucide (CLAUDE.md: Phosphor
                  first). At 14-16px an outline ⓘ is a 1.5px ring around a dot
                  and a stem that merge into a smudge -- rendered side by side,
                  lucide/regular/bold/duotone are all indistinguishable from a
                  question mark, which is exactly what it was read as. The solid
                  counter is the only weight where the "i" survives at this
                  size. */}
              <Info
                weight="fill"
                className={cn('size-4', iconClassName)}
                aria-hidden
              />
            </button>
          }
        />
        <TooltipContent side={side} className="max-w-[260px]">
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
